// src/pages/Profile.jsx  →  Order Tracker (no auth required)
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package,
  Search,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  MapPin,
  ArrowLeft,
  AlertCircle,
  ShoppingBag,
} from 'lucide-react';
import { OrderService } from '../services/orders/orderService';

const statusMeta = {
  delivered:  { icon: CheckCircle, color: 'text-green-400',  bg: 'bg-green-500/10  border-green-500/30'  },
  shipped:    { icon: Truck,        color: 'text-blue-400',   bg: 'bg-blue-500/10   border-blue-500/30'   },
  processing: { icon: Clock,        color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30' },
  cancelled:  { icon: XCircle,      color: 'text-red-400',    bg: 'bg-red-500/10    border-red-500/30'    },
  pending:    { icon: Clock,        color: 'text-gray-400',   bg: 'bg-gray-500/10   border-gray-500/30'   },
};

const Profile = () => {
  const [orderId, setOrderId] = useState('');
  const [order, setOrder]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    const trimmed = orderId.trim();
    if (!trimmed) return;
    setError('');
    setOrder(null);
    setLoading(true);
    try {
      const found = await OrderService.getOrderById(trimmed);
      if (!found) throw new Error('Order not found.');
      setOrder(found);
    } catch (err) {
      setError(err.message || 'Order not found. Please check the ID and try again.');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (date) => {
    if (!date) return '—';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const meta = order ? (statusMeta[order.status] || statusMeta.pending) : null;
  const StatusIcon = meta?.icon;

  return (
    <div className="min-h-screen bg-brand-black pt-24 pb-20 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto">

        {/* Back link */}
        <Link
          to="/kganya-scents"
          className="inline-flex items-center gap-2 text-sm text-brand-light/50 hover:text-brand-gold transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to shop
        </Link>

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h1 className="text-4xl font-heading text-brand-gold mb-2">Track Your Order</h1>
          <p className="text-brand-light/50 font-body text-sm">
            Enter your order ID — found in your confirmation email — to see the latest status.
          </p>
        </motion.div>

        {/* Search form */}
        <motion.form
          onSubmit={handleSearch}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="flex gap-3 mb-8"
        >
          <input
            type="text"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="e.g. PY17148293840012"
            className="flex-1 px-4 py-3 bg-brand-black border border-white/10 focus:border-brand-gold/50 rounded-xl text-brand-light placeholder:text-brand-light/30 text-sm outline-none transition-colors"
          />
          <button
            type="submit"
            disabled={loading || !orderId.trim()}
            className="px-5 py-3 bg-brand-red hover:bg-brand-red/80 disabled:opacity-40 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
          >
            {loading ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            Track
          </button>
        </motion.form>

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm mb-6"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </motion.div>
        )}

        {/* Order result */}
        {order && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-white/8 bg-white/3 backdrop-blur-xl overflow-hidden"
          >
            {/* Status header */}
            <div className={`flex items-center justify-between px-6 py-5 border-b border-white/8 ${meta.bg}`}>
              <div>
                <p className="text-xs text-brand-light/40 uppercase tracking-widest mb-1">Order</p>
                <p className="text-brand-gold font-heading text-lg">#{order.orderNumber}</p>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${meta.bg} ${meta.color}`}>
                <StatusIcon className="w-4 h-4" />
                <span className="text-sm font-medium capitalize">{order.status}</span>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-brand-light/40 uppercase tracking-wider mb-1">Placed</p>
                  <p className="text-sm text-brand-light">{fmt(order.createdAt)}</p>
                </div>
                {order.tracking?.estimatedDelivery && (
                  <div>
                    <p className="text-xs text-brand-light/40 uppercase tracking-wider mb-1">Est. Delivery</p>
                    <p className="text-sm text-brand-light">{fmt(order.tracking.estimatedDelivery)}</p>
                  </div>
                )}
              </div>

              {/* Items */}
              <div>
                <p className="text-xs text-brand-light/40 uppercase tracking-wider mb-3">Items</p>
                <div className="space-y-2">
                  {order.items?.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/4">
                      {item.image && (
                        <img src={item.image} alt={item.name} className="w-10 h-10 rounded-lg object-contain bg-white/5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-brand-light truncate">{item.name}</p>
                        <p className="text-xs text-brand-light/40">Qty {item.quantity} · {item.price}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping */}
              {order.shippingAddress && (
                <div>
                  <p className="text-xs text-brand-light/40 uppercase tracking-wider mb-2">Shipping to</p>
                  <div className="flex items-start gap-2 text-sm text-brand-light/70">
                    <MapPin className="w-4 h-4 text-brand-gold mt-0.5 flex-shrink-0" />
                    <span>
                      {order.shippingAddress.firstName} {order.shippingAddress.lastName},&nbsp;
                      {order.shippingAddress.address}, {order.shippingAddress.city},&nbsp;
                      {order.shippingAddress.province} {order.shippingAddress.postalCode}
                    </span>
                  </div>
                </div>
              )}

              {/* Tracking number */}
              {order.tracking?.trackingNumber && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-brand-gold/5 border border-brand-gold/15">
                  <Truck className="w-4 h-4 text-brand-gold" />
                  <div>
                    <p className="text-xs text-brand-light/40 uppercase tracking-wider">Tracking #</p>
                    <p className="text-sm text-brand-gold font-medium">{order.tracking.trackingNumber}</p>
                  </div>
                </div>
              )}

              {/* Total */}
              <div className="flex justify-between items-center pt-4 border-t border-white/8">
                <p className="text-sm text-brand-light/50">Order Total</p>
                <p className="text-xl font-heading text-brand-gold">R{order.total?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Empty state */}
        {!order && !loading && !error && (
          <div className="text-center py-16 text-brand-light/20">
            <Package className="w-12 h-12 mx-auto mb-4" />
            <p className="text-sm">Your order details will appear here</p>
            <Link
              to="/kganya-scents"
              className="inline-flex items-center gap-2 mt-6 text-sm text-brand-gold/60 hover:text-brand-gold transition-colors"
            >
              <ShoppingBag className="w-4 h-4" />
              Browse Kganya Scents
            </Link>
          </div>
        )}

      </div>
    </div>
  );
};

export default Profile;

